import { useEffect, useMemo, useState } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, GeoJSON, useMap, Circle, CircleMarker, Tooltip, Pane } from "react-leaflet";
import { Button } from "@/components/ui/button";
import { UploadedLayers } from "@/components/map/UploadedLayers";
import { MeasureLayer } from "@/components/map/MeasureTool";
import { MapEvents } from "@/components/map/CoordinateBar";
import { NoteMarkers } from "@/components/map/FieldNotes";
import { DrawLayer, DrawingsLayer } from "@/components/map/DrawTool";

window.L = L;

const CG_CENTER = [21.27, 81.87];
const TYPE_COLORS = { Agricultural: "#10B981", Irrigated: "#34D399", Residential: "#F59E0B", Commercial: "#C85A32", Forest: "#047857", Barren: "#94A3B8" };

function FitToData({ data }) {
  const map = useMap();
  useMemo(() => {
    if (data?.features?.length) {
      const coords = data.features.flatMap((f) => f.geometry.coordinates[0].map(([lng, lat]) => [lat, lng]));
      map.fitBounds(coords, { padding: [60, 60], maxZoom: 14 });
    }
  }, [data, map]);
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    if (target.zoomDelta) { map.zoomIn(target.zoomDelta > 0 ? target.zoomDelta : undefined); if (target.zoomDelta < 0) map.zoomOut(-target.zoomDelta); return; }
    if (target.bounds) map.flyToBounds(target.bounds, { padding: [40, 40], maxZoom: target.maxZoom ?? 17, duration: 1.2 });
    else if (target.center) map.flyTo(target.center, target.zoom ?? 14, { duration: 1.2 });
  }, [target, map]);
  return null;
}

export function LeafletMap({ data, onSelect, interactive = true, className = "", extraLayers = [], flyTo = null, measure = null, onView, onCursor, myLocation = null, notes = [], onDeleteNote, onMapClick, draw = null, drawings = [], onDeleteDrawing, drawEditing = null, onDrawEditPoints, onDrawEdit, showDrawLabels = true, drawSnapTargets = [], drawSnapOn = false, highlight = null }) {
  const [satellite, setSatellite] = useState(true);
  const style = (f) => ({ color: TYPE_COLORS[f.properties.land_type] || "#10B981", weight: 2, fillOpacity: 0.35 });
  const onEach = (feature, layer) => {
    layer.on({
      click: (e) => { if (window.__cglandMeasuring) return; window.L.DomEvent.stopPropagation(e); onSelect?.({ ...feature.properties, geometry: feature.geometry }); },
      mouseover: (e) => e.target.setStyle({ weight: 3, fillOpacity: 0.6 }),
      mouseout: (e) => e.target.setStyle(style(feature)),
    });
    layer.bindTooltip(`<b>Khasra ${feature.properties.khasra}</b><br/>${feature.properties.village}, ${feature.properties.district}`, { sticky: true });
  };
  return (
    <div className={`relative h-full w-full ${className}`} data-testid="leaflet-map">
      <MapContainer center={CG_CENTER} zoom={interactive ? 7 : 8} className="h-full w-full" zoomControl={interactive} dragging={interactive} scrollWheelZoom={interactive} doubleClickZoom={interactive} attributionControl={interactive}>
        {satellite ? (
          <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution="Tiles © Esri" />
        ) : (
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap contributors" />
        )}
        {data && <GeoJSON key={JSON.stringify(data.features?.map((f) => f.properties.parcel_id))} data={data} style={style} onEachFeature={onEach} />}
        {data && <FitToData data={data} />}
        <UploadedLayers layers={extraLayers} onSelect={onSelect} />
        {highlight && (
          <Pane name="highlight" style={{ zIndex: 640, pointerEvents: "none" }}>
            <GeoJSON key={JSON.stringify(highlight).slice(0, 400)} data={highlight} interactive={false} className="selected-glow"
              style={() => ({ color: "#FDE047", weight: 4, fillColor: "#FDE047", fillOpacity: 0.18, opacity: 1 })}
              pointToLayer={(f, latlng) => window.L.circleMarker(latlng, { radius: 11, color: "#FDE047", weight: 4, fillOpacity: 0.2, className: "selected-glow" })} />
          </Pane>
        )}
        <FlyTo target={flyTo} />
        {interactive && <MapEvents onView={onView} onCursor={onCursor} onClick={onMapClick} />}
        {notes.length > 0 && <NoteMarkers notes={notes} onDelete={onDeleteNote} />}
        {(drawings.length > 0 || draw || measure) && (
          <Pane name="sketch" style={{ zIndex: 450 }}>
            {drawings.length > 0 && <DrawingsLayer drawings={drawings} onDelete={onDeleteDrawing} editing={drawEditing} onEditPoints={onDrawEditPoints} onEdit={onDrawEdit} showLabels={showDrawLabels} snapTargets={drawSnapTargets} snapOn={drawSnapOn} />}
            {draw && <DrawLayer draw={draw} />}
            {measure && <MeasureLayer mode={measure.mode} points={measure.points} onPoints={measure.setPoints} onSummary={measure.setSummary} locked={measure.locked} onFinish={measure.finish} />}
          </Pane>
        )}
        {myLocation && (
          <>
            <Circle center={[myLocation.lat, myLocation.lng]} radius={myLocation.accuracy} pathOptions={{ color: "#2563EB", weight: 1, fillColor: "#3B82F6", fillOpacity: 0.15 }} />
            <CircleMarker center={[myLocation.lat, myLocation.lng]} radius={8} pathOptions={{ color: "#fff", weight: 3, fillColor: "#2563EB", fillOpacity: 1 }}>
              <Tooltip direction="top" offset={[0, -10]}>You are here (±{Math.round(myLocation.accuracy)} m)</Tooltip>
            </CircleMarker>
          </>
        )}
      </MapContainer>
      {interactive && (
        <div className="absolute top-3 right-3 z-[1000] flex rounded-full overflow-hidden border border-slate-300 bg-white shadow-lg text-xs font-semibold" data-no-export="true">
          <Button data-testid="basemap-street-button" size="sm" variant="ghost" className={`rounded-none h-8 ${!satellite ? "bg-emerald-900 text-white hover:bg-emerald-900 hover:text-white" : "text-slate-700"}`} onClick={() => setSatellite(false)}>Street</Button>
          <Button data-testid="basemap-satellite-button" size="sm" variant="ghost" className={`rounded-none h-8 ${satellite ? "bg-emerald-900 text-white hover:bg-emerald-900 hover:text-white" : "text-slate-700"}`} onClick={() => setSatellite(true)}>Satellite</Button>
        </div>
      )}
    </div>
  );
}
